import type { DamageEvent, GameState, PlayerState, ShotResult } from '../shared/types';
import { sourceFallY } from '../shared/walk-geometry';

export interface PresentedPlayer extends PlayerState { renderAlive: boolean; deathProgress: number }
type Pose = { x: number; y: number };
const clamp = (value: number) => Math.max(0, Math.min(1, value));

export function damageEventAt(event: DamageEvent, shot: ShotResult): number {
  const at = Number.isFinite(event.at) ? event.at : shot.impacts[0]?.at ?? shot.duration;
  // A server START_MOVE can carry final IsLiving=false while the local body
  // still falls. It is not a blood damage death at contact time.
  const fall = event.killed && event.amount === 0
    ? shot.playerTransitions?.find((entry) => entry.playerId === event.playerId && entry.at === at && entry.fall?.hpBeforeDeath !== undefined)?.fall : null;
  return fall ? fall.startedAt + fall.duration : at;
}

/** Presentation clock only; every collision and final coordinate is authoritative. */
export function combatPlayers(previous: GameState | null, current: GameState, shot: ShotResult | null, elapsed: number): PresentedPlayer[] {
  if (!previous || !shot || elapsed >= shot.duration) return current.players.map((player) => ({ ...player, renderAlive: player.alive, deathProgress: player.alive ? 0 : 1 }));
  const transitions = [...shot.playerTransitions ?? []].sort((a, b) => a.at - b.at);
  const damage = [...shot.damage].sort((a, b) => damageEventAt(a, shot) - damageEventAt(b, shot));
  return current.players.map((authoritative) => {
    const old = previous.players.find((player) => player.id === authoritative.id);
    if (!old) return { ...authoritative, renderAlive: authoritative.alive, deathProgress: authoritative.alive ? 0 : 1 };
    const visible: PresentedPlayer = {
      ...authoritative, x: old.x, y: old.y, face: old.face, hp: old.hp, alive: old.alive,
      frozenTurns: old.frozenTurns, anger: old.anger, energy: old.energy,
      renderAlive: old.alive, deathProgress: old.alive ? 0 : 1,
    };
    let motion: { from: Pose; to: Pose; at: number; duration: number; sourceFall?: boolean } | null = null;
    let destination: Pose = { x: visible.x, y: visible.y };
    let deathAt: number | null = null;
    let flyTranslated = false;
    const position = (time: number): Pose => {
      if (!motion) return { x: visible.x, y: visible.y };
      if (motion.sourceFall) return { x: motion.to.x, y: sourceFallY(motion.from.y, motion.to.y, time - motion.at) };
      const fraction = clamp((time - motion.at) / motion.duration);
      const progress = motion.to.y > motion.from.y + 8 ? fraction * fraction : fraction * fraction * (3 - 2 * fraction);
      return { x: motion.from.x + (motion.to.x - motion.from.x) * progress, y: motion.from.y + (motion.to.y - motion.from.y) * progress };
    };
    const playerTransitions = transitions.filter((transition) => transition.playerId === authoritative.id);
    for (const transition of playerTransitions) {
      if (transition.at > elapsed) continue;
      let before = position(transition.at);
      // BombAction.TRANSLATE -> GameLiving.__transmit sets info.pos instantly.
      // The next ordered snapshot is START_MOVE: only that landing displacement
      // is animated. Old snapshots with one position simply teleport there.
      if (shot.kind === 'fly' && authoritative.id === shot.playerId && transition.at > 0 && !flyTranslated) {
        visible.x = transition.x; visible.y = transition.y;
        destination = { x: visible.x, y: visible.y }; motion = null;
        before = destination; flyTranslated = true;
      }
      const fall = transition.fall;
      if (fall) {
        if (elapsed >= fall.startedAt) {
          motion = { from: fall.from, to: fall.to, at: fall.startedAt, duration: fall.duration, sourceFall: true };
          destination = fall.to;
        }
      } else if (transition.alive && (transition.x !== destination.x || transition.y !== destination.y)) {
        motion = { from: before, to: { x: transition.x, y: transition.y }, at: transition.at, duration: 220 };
        destination = { x: transition.x, y: transition.y };
      }
      visible.face = transition.face;
      visible.energy = transition.energy;
      // Impact records may be emitted in engine processing order rather than
      // time order. HP is accumulated from timed damage below, never assigned
      // an older absolute snapshot that could temporarily heal the player.
      visible.anger = transition.at === 0 ? transition.anger : Math.max(visible.anger, transition.anger);
      if (!transition.alive && deathAt === null) deathAt = fall?.hpBeforeDeath !== undefined ? fall.startedAt + fall.duration : transition.at;
    }
    if (!playerTransitions.length && (old.x !== authoritative.x || old.y !== authoritative.y)) {
      const at = shot.impacts.at(-1)?.at ?? shot.duration;
      if (elapsed >= at) {
        if (shot.kind === 'fly' && authoritative.id === shot.playerId) { visible.x = authoritative.x; visible.y = authoritative.y; }
        else motion = { from: { x: old.x, y: old.y }, to: { x: authoritative.x, y: authoritative.y }, at, duration: 220 };
      }
    }
    for (const event of damage) {
      if (event.playerId !== authoritative.id || damageEventAt(event, shot) > elapsed) continue;
      visible.hp = Math.max(0, visible.hp - event.amount);
      if (event.frozen) visible.frozenTurns = Math.max(1, visible.frozenTurns);
      if (event.thawed) visible.frozenTurns = 0;
      if (event.killed && deathAt === null) deathAt = damageEventAt(event, shot);
    }
    if (deathAt !== null) {
      if (elapsed >= deathAt) {
        const deathPose = position(deathAt);
        visible.x = deathPose.x; visible.y = deathPose.y; motion = null;
        visible.alive = false; visible.hp = 0;
        visible.deathProgress = 1; visible.renderAlive = false;
      } else {
        const fall = playerTransitions.find((entry) => !entry.alive && entry.fall?.hpBeforeDeath !== undefined)?.fall;
        if (fall) visible.hp = fall.hpBeforeDeath!;
      }
    }
    const posed = position(elapsed); visible.x = posed.x; visible.y = posed.y;
    return visible;
  });
}
